import { useEffect, useCallback, useRef, useState } from 'react';
import { useBoolState } from '@/hooks/state/useBoolState';
import { DRIVER_PORTAL_ROOT_CLASS } from '@/utils/constants/ui/driver';
/**
 * Driver スタイル制御
 */
export const useDriverStyle = (options) => {
    const { activeIndex, isShow, settings } = options;
    const { tooltip } = settings[activeIndex];
    /** リサイズ前 画面幅 */
    const lastInnerWidthRef = useRef(undefined);
    if (lastInnerWidthRef !== undefined) {
        lastInnerWidthRef.current = window.innerWidth;
    }
    // オーバーレイ clipPath
    const [clipPath, changeClipPath] = useState('');
    // オーバーレイ アニメーション
    const [transition, changeTransition] = useState(undefined);
    // ツールチップ 表示
    const { boolState: isShowTooltip, changeFalse: hideTooltip, changeTrue: showTooltip, } = useBoolState({
        initialState: true,
    });
    // ツールチップ 表示位置
    const [tooltipPosition, changeTooltipPosition] = useState(undefined);
    /**
     * ハイライト位置 取得
     * @param targetRef 表示対象の ref
     */
    const getHighlightPosition = useCallback((targetRef) => {
        const targetElm = targetRef.current;
        const wrapElm = document.getElementsByClassName(DRIVER_PORTAL_ROOT_CLASS)[0];
        if (!targetElm || !wrapElm) {
            return {};
        }
        // 対象要素の width, height
        const { clientHeight, clientWidth } = targetElm;
        // 対象要素 の位置
        const { left, top } = targetElm.getBoundingClientRect();
        // relative 指定要素 の位置
        const { left: wrapLeft, top: wrapTop } = wrapElm.getBoundingClientRect();
        const positionLeft = left - wrapLeft;
        const positionTop = top - wrapTop;
        return {
            bottom: positionTop + clientHeight,
            left: positionLeft,
            right: positionLeft + clientWidth,
            top: positionTop,
        };
    }, []);
    /**
     * オーバーレイ ClipPath 取得
     * @param targetRef 表示対象の ref
     */
    const getClipPath = useCallback((targetRef) => {
        const { bottom, left, right, top } = getHighlightPosition(targetRef);
        return `polygon(
        0% 0%,
        0% 100%,
        ${left}px 100%,
        ${left}px ${top}px,
        ${right}px ${top}px,
        ${right}px ${bottom}px,
        ${left}px ${bottom}px,
        ${left}px 100%,
        100% 100%,
        100% 0%
      )`;
    }, [getHighlightPosition]);
    /**
     * ツールチップ 表示位置 取得
     * @param targetRef 表示対象の ref
     */
    const getTooltipPosition = useCallback((targetRef) => {
        const { left, top } = getHighlightPosition(targetRef);
        return top === undefined || left === undefined
            ? undefined
            : { left: `${left}px`, top: `${top}px` };
    }, [getHighlightPosition]);
    /**
     * 位置更新
     * @param targetRef 表示対象の ref
     */
    const updateStyle = useCallback((targetRef) => {
        changeClipPath(getClipPath(targetRef));
        if (tooltip) {
            changeTooltipPosition(getTooltipPosition(targetRef));
        }
    }, [
        changeClipPath,
        changeTooltipPosition,
        getClipPath,
        getTooltipPosition,
        tooltip,
    ]);
    // リサイズ時にスタイル更新
    useEffect(() => {
        const { ref } = settings[activeIndex];
        // 表示対象が存在しない場合 処理終了
        if (!isShow || !(ref === null || ref === void 0 ? void 0 : ref.current)) {
            return () => { };
        }
        updateStyle(ref);
        const onResize = () => {
            const lastInnerWidth = lastInnerWidthRef.current;
            const currentInnerWidth = window.innerWidth;
            // 横幅リサイズ時のみスタイル調整
            if (lastInnerWidth !== currentInnerWidth) {
                lastInnerWidthRef.current = currentInnerWidth;
                updateStyle(ref);
            }
        };
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [activeIndex, isShow, settings, updateStyle]);
    return {
        changeTransition,
        clipPath,
        hideTooltip,
        isShowTooltip,
        showTooltip,
        tooltipPosition,
        transition,
        updateStyle,
    };
};
